import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/ui";
import { Loader2, Users, CalendarClock, Sparkles, ChevronRight, Search, MapPin, Trophy, Star, Bell, TrendingUp } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function FanHome() {
  const m = useIsMobile();
  const nav = useNavigate();
  const { user } = useAuth();
  const [following, setFollowing] = useState(null);
  const [totals, setTotals] = useState({});
  const [upcoming, setUpcoming] = useState(null);
  const [results, setResults] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [mostWatched, setMostWatched] = useState([]);

  useEffect(() => {
    api.get("/competitors/following").then((r) => setFollowing(r.data || [])).catch(() => setFollowing([]));
    api.get("/competitors/following-totals").then((r) => setTotals(r.data?.totals || {})).catch(() => {});
    api.get("/competitors/following-upcoming").then((r) => setUpcoming(r.data?.upcoming || [])).catch(() => setUpcoming([]));
    api.get("/competitors/following-results").then((r) => setResults(r.data?.results || [])).catch(() => {});
    api.get("/me/favorite-rodeos").then((r) => setFavorites(r.data?.rodeos || [])).catch(() => {});
    api.get("/alert-prefs").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/rider-media/most-watched").then((r) => setMostWatched(r.data?.riders || [])).catch(() => {});
  }, []);

  const savedPrefs = (next) => {
    setPrefs(next);
    api.put("/alert-prefs", next).catch(() => {});
  };
  const removeFav = (id) => {
    setFavorites((f) => f.filter((x) => x.id !== id));
    api.delete(`/me/favorite-rodeos/${id}`).catch(() => {});
  };

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const loading = following === null || upcoming === null;
  const hasRiders = Array.isArray(following) && following.length > 0;
  const runningToday = (upcoming || []).filter((x) => x.is_today);

  const SectionHead = ({ icon: Icon, children, badge, action }) => (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#C8000F]" />
      <h2 className={`font-display font-bold text-sm uppercase tracking-wide ${title}`}>{children}</h2>
      {badge != null && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">{badge}</span>}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );

  return (
    <div className={page} data-testid="fan-home">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>
          {user?.name ? `Howdy, ${user.name.split(" ")[0]}` : "Following the action"}
        </h1>
        <p className={`text-sm mt-1 ${sub}`}>Your riders, where they run next, and this week in rodeo.</p>
      </div>

      <div className="p-4 sm:p-6 max-w-4xl space-y-6">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : !hasRiders ? (
          <div className={`rounded-xl border p-6 text-center ${card}`} data-testid="fan-empty">
            <Users className="w-8 h-8 text-[#C8000F] mx-auto mb-3" />
            <div className={`font-display font-bold text-lg ${title}`}>Follow your first rider</div>
            <p className={`text-sm mt-1 mb-4 ${sub}`}>Pick the cowboys and cowgirls you want to follow and we'll keep you on their every run.</p>
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => nav("/riders")} data-testid="fan-find-riders"
                className="inline-flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
                <Search className="w-4 h-4" /> Find riders
              </button>
              <button onClick={() => nav("/weekly")} data-testid="fan-discover"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg border ${m ? "border-white/15 text-slate-200" : "border-[#D7DBE0] text-[#14181F]"}`}>
                <Sparkles className="w-4 h-4" /> Discover
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Running today strip */}
            {runningToday.length > 0 && (
              <section data-testid="fan-running-today">
                <div className="flex items-center gap-2 mb-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8000F] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C8000F]" />
                  </span>
                  <h2 className={`font-display font-bold text-sm uppercase tracking-wide ${title}`}>Running today</h2>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">{runningToday.length}</span>
                </div>
                <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
                  {runningToday.map((x, i) => (
                    <button key={`${x.rodeo_id}-${x.rider}-${i}`} onClick={() => nav(`/rodeo/${x.rodeo_id}`)} data-testid={`fan-today-${i}`}
                      className={`shrink-0 w-56 text-left rounded-xl border p-3 ${card} ring-1 ring-[#C8000F]/30`}>
                      <div className={`font-bold text-sm truncate ${title}`}>{x.rider}</div>
                      <div className={`text-[11px] truncate ${sub}`}>{x.event}{x.performance_label ? ` · ${x.performance_label}` : ""}{x.position ? ` · #${x.position}` : ""}</div>
                      <div className={`text-[11px] truncate mt-1 flex items-center gap-1 ${sub}`}><MapPin className="w-3 h-3 shrink-0" />{x.rodeo_name}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Your riders */}
            <section>
              <SectionHead icon={Users} badge={following.length} action={
                <button onClick={() => nav("/riders")} data-testid="fan-manage-riders" className="text-[11px] font-semibold text-[#C8000F] hover:underline flex items-center gap-0.5">Manage <ChevronRight className="w-3.5 h-3.5" /></button>
              }>Your riders</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {following.map((name) => {
                  const t = totals[name];
                  return (
                    <button key={name} onClick={() => nav(`/riders`)} data-testid={`fan-rider-${name.replace(/\s/g, "-")}`}
                      className={`text-left rounded-xl border p-3 flex items-center justify-between gap-2 ${card} ${m ? "active:bg-white/5" : "hover:bg-[#FAFBFC]"}`}>
                      <div className="min-w-0">
                        <div className={`font-semibold text-sm truncate ${title}`}>{name}</div>
                        {t && <div className={`text-[11px] ${sub}`}>Season + CFR {money(t.total)}</div>}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </section>

            {/* How your riders did */}
            {results.length > 0 && (
              <section>
                <SectionHead icon={Trophy} badge={results.length}>How your riders did</SectionHead>
                <div className={`rounded-xl border overflow-hidden ${card}`}>
                  {results.slice(0, 15).map((x, i) => (
                    <button key={`${x.rodeo_id}-${x.rider}-${i}`} onClick={() => nav(`/rodeo/${x.rodeo_id}`)} data-testid={`fan-result-${i}`}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-t transition-colors ${m ? "border-white/5 active:bg-white/5" : "border-[#EEF0F2] hover:bg-[#FAFBFC]"} ${i === 0 ? "border-t-0" : ""}`}>
                      <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-display font-extrabold text-xs ${["1"].includes(String(x.place)) ? "bg-[#E6B35A]/25 text-[#B4801F]" : "bg-[#C8000F]/10 text-[#C8000F]"}`}>{x.place_label}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm truncate ${title}`}>{x.rider} <span className={`font-normal ${sub}`}>· {x.event}</span></div>
                        <div className={`text-[11px] truncate ${sub}`}>{x.rodeo_name}{x.date ? ` · ${fmtDate(x.date)}` : ""}</div>
                      </div>
                      {x.earnings > 0 && <div className="text-right shrink-0 text-sm font-bold text-[#3B6B1E]">{money(x.earnings)}</div>}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Where your riders compete next */}
            <section>
              <SectionHead icon={CalendarClock} badge={upcoming.length || undefined}>Where your riders run next</SectionHead>
              <div className={`rounded-xl border overflow-hidden ${card}`}>
                {upcoming.length === 0 ? (
                  <div className={`px-3 py-8 text-center text-sm ${sub}`}>No upcoming draws posted yet for your riders.</div>
                ) : upcoming.slice(0, 25).map((x, i) => (
                  <button key={`${x.rodeo_id}-${x.rider}-${i}`} onClick={() => nav(`/rodeo/${x.rodeo_id}`)} data-testid={`fan-next-${i}`}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-t transition-colors ${m ? "border-white/5 active:bg-white/5" : "border-[#EEF0F2] hover:bg-[#FAFBFC]"} ${i === 0 ? "border-t-0" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${title}`}>{x.rider} <span className={`font-normal ${sub}`}>· {x.event}</span></div>
                      <div className={`text-[11px] truncate ${sub} flex items-center gap-1`}>
                        <MapPin className="w-3 h-3 shrink-0" />{x.rodeo_name}
                        {[x.city, x.state].filter(Boolean).length ? <span> · {[x.city, x.state].filter(Boolean).join(", ")}</span> : null}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {x.performance_date && <div className="text-[11px] font-semibold text-[#C8000F]">{fmtDate(x.performance_date)}</div>}
                      {x.performance_label && <div className={`text-[10px] ${sub}`}>{x.performance_label}{x.position ? ` · #${x.position}` : ""}</div>}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* Your rodeos (favorites) */}
            <section>
              <SectionHead icon={Star} badge={favorites.length || undefined}>Your rodeos</SectionHead>
              {favorites.length === 0 ? (
                <div className={`rounded-xl border p-4 text-sm ${card} ${sub}`} data-testid="fan-favorites-empty">
                  Tap the <Star className="w-3.5 h-3.5 inline -mt-0.5 text-[#C8000F]" /> on any rodeo to save it here and keep an eye on it.
                </div>
              ) : (
                <div className={`rounded-xl border overflow-hidden ${card}`}>
                  {favorites.map((r, i) => (
                    <div key={r.id} data-testid={`fan-fav-${i}`}
                      className={`flex items-center gap-3 px-3 py-2.5 border-t ${m ? "border-white/5" : "border-[#EEF0F2]"} ${i === 0 ? "border-t-0" : ""}`}>
                      <button onClick={() => nav(`/rodeo/${r.id}`)} className="flex-1 min-w-0 text-left">
                        <div className={`font-semibold text-sm truncate ${title}`}>{r.name}</div>
                        <div className={`text-[11px] truncate ${sub}`}>
                          {[r.city, r.state].filter(Boolean).join(", ")}
                          {r.your_riders?.length ? <span className="text-[#C8000F] font-semibold"> · {r.your_riders.length} of your riders entered</span> : null}
                        </div>
                      </button>
                      <button onClick={() => removeFav(r.id)} data-testid={`fan-fav-remove-${i}`} aria-label="Remove favorite" className="shrink-0 p-1.5 rounded-full hover:bg-[#C8000F]/10">
                        <Star className="w-4 h-4 fill-[#C8000F] text-[#C8000F]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Rider alerts settings */}
            {prefs && (
              <section>
                <SectionHead icon={Bell}>Rider alerts</SectionHead>
                <div className={`rounded-xl border divide-y ${card} ${m ? "divide-white/5" : "divide-[#EEF0F2]"}`}>
                  {[
                    { k: "rider_up", label: "When a rider I follow is up next", hint: "Drawn to run today or tomorrow" },
                    { k: "rider_placed", label: "When a rider I follow places", hint: "A top result is posted" },
                  ].map((row) => {
                    const on = prefs.enabled && prefs.riders && prefs[row.k];
                    return (
                      <div key={row.k} className="flex items-center justify-between gap-3 px-3 py-3">
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold ${title}`}>{row.label}</div>
                          <div className={`text-[11px] ${sub}`}>{row.hint}</div>
                        </div>
                        <button data-testid={`fan-alert-${row.k}`} onClick={() => savedPrefs({ ...prefs, enabled: true, riders: true, [row.k]: !on })}
                          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-[#C8000F]" : m ? "bg-white/15" : "bg-slate-300"}`}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Most watched this week */}
            {mostWatched.length > 0 && (
              <section data-testid="fan-most-watched">
                <SectionHead icon={TrendingUp}>Most watched this week</SectionHead>
                <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
                  {mostWatched.slice(0, 10).map((x, i) => (
                    <button key={x.rider} onClick={() => nav("/riders")} data-testid={`fan-watched-${i}`}
                      className={`shrink-0 w-44 text-left rounded-xl border p-3 ${card}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-display font-extrabold text-[#C8000F] text-sm">#{i + 1}</span>
                        <div className={`font-bold text-sm truncate ${title}`}>{x.rider}</div>
                      </div>
                      <div className={`text-[11px] mt-0.5 ${sub}`}>{x.plays} play{x.plays === 1 ? "" : "s"} · {x.opens} view{x.opens === 1 ? "" : "s"}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Weekly Discovery teaser */}
            <section>
              <button onClick={() => nav("/weekly")} data-testid="fan-weekly-teaser"
                className={`w-full text-left rounded-xl border p-4 flex items-center gap-3 ${card} ring-1 ring-[#C8000F]/20`}>
                <Sparkles className="w-6 h-6 text-[#C8000F] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className={`font-display font-bold ${title}`}>Weekly Discovery</div>
                  <div className={`text-[12px] ${sub}`}>Who beat who, recommended riders and this week's standout runs.</div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
